// frontend/src/components/Provider/ProviderSlugSetupForm.tsx
// FIXED: Updated to use new hooks and API structure

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Globe,
  Check,
  X,
  AlertCircle,
  Info,
  RefreshCw,
  Save,
  Loader2
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { 
  useCheckSlugAvailability, 
  useUpdateSlug, 
  useGenerateSlugSuggestions 
} from '@/hooks/api/useProviderQueries';

interface ProviderSlugSetupFormProps {
  businessName?: string;
  onSlugSet: (slug: string) => void;
  className?: string;
}

const ProviderSlugSetupForm: React.FC<ProviderSlugSetupFormProps> = ({
  businessName = '',
  onSlugSet,
  className = ''
}) => {
  const [slug, setSlug] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [availabilityMessage, setAvailabilityMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);

  // React Query hooks
  const checkSlugMutation = useCheckSlugAvailability();
  const updateSlugMutation = useUpdateSlug();
  const generateSuggestionsMutation = useGenerateSlugSuggestions();

  const baseUrl = window.location.origin;
  const fullUrl = slug ? `${baseUrl}/providers/${slug}` : '';

  // Debounced slug validation
  useEffect(() => {
    if (!slug || slug.length < 3) {
      setIsAvailable(null);
      setAvailabilityMessage('');
      return;
    }

    if (!isValidSlug(slug)) {
      setIsAvailable(false);
      setAvailabilityMessage('Invalid format. Use only lowercase letters, numbers, and hyphens.');
      return;
    }

    const timeoutId = setTimeout(() => {
      checkSlugAvailability(slug);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [slug]);

  const isValidSlug = (value: string): boolean => {
    // Must be 3-50 characters, lowercase letters, numbers, hyphens only
    // Cannot start or end with hyphens
    const slugRegex = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;
    return value.length >= 3 && value.length <= 50 && slugRegex.test(value);
  };

  const checkSlugAvailability = async (slugToCheck: string) => {
    if (!slugToCheck || !isValidSlug(slugToCheck)) return;

    setIsChecking(true);
    setIsAvailable(null);

    try {
      const result = await checkSlugMutation.mutateAsync(slugToCheck);
      
      if (result.available) {
        setIsAvailable(true);
        setAvailabilityMessage('Great! This URL is available.');
      } else {
        setIsAvailable(false);
        setAvailabilityMessage('This URL is already taken. Please choose another.');
      }
    } catch (error: any) {
      console.error('Error checking slug availability:', error);
      setIsAvailable(false);
      setAvailabilityMessage('Error checking availability. Please try again.');
      toast.error('Failed to check URL availability');
    } finally {
      setIsChecking(false);
    }
  };

  const generateSuggestion = useCallback(async () => {
    if (!businessName.trim()) {
      toast.error('Please enter a business name first');
      return;
    }

    try {
      const result = await generateSuggestionsMutation.mutateAsync(businessName);
      
      if (result.suggestions && result.suggestions.length > 0) {
        setSuggestions(result.suggestions);
        // Auto-select the first suggestion
        const firstSuggestion = result.suggestions[0];
        setSlug(firstSuggestion);
        toast.success('Suggestions generated!');
      } else {
        toast.info('No suggestions available. Try creating a custom URL.');
      }
    } catch (error: any) {
      console.error('Error generating suggestions:', error);
      toast.error('Failed to generate suggestions');
    }
  }, [businessName, generateSuggestionsMutation]);

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.toLowerCase().trim();
    
    // Remove invalid characters and replace spaces with hyphens
    value = value
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');

    setSlug(value);
    setSuggestions([]); // Clear suggestions when user types
  };

  const handleSave = async () => {
    if (!slug || !isAvailable) {
      toast.error('Please enter a valid and available URL');
      return;
    }

    setIsSaving(true);

    try {
      const result = await updateSlugMutation.mutateAsync(slug);
      
      if (result.provider) {
        toast.success('URL set successfully!');
        onSlugSet(slug);
      }
    } catch (error: any) {
      console.error('Error setting slug:', error);
      toast.error(error.message || 'Failed to set URL. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setSlug(suggestion);
    setSuggestions([]);
  };

  return (
    <div className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
      <div className="space-y-4">
        <div>
          <div className="flex items-center mb-2">
            <Globe className="h-5 w-5 text-blue-600 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Set Your Profile URL</h3>
          </div>
          <p className="text-sm text-gray-600">
            Choose a unique URL for your provider profile. This will be how customers find you.
          </p>
        </div>

        {/* URL Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Your Profile URL
          </label>
          <div className="flex items-center">
            <span className="inline-flex items-center px-3 py-2 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
              {baseUrl}/providers/
            </span>
            <input
              type="text"
              value={slug}
              onChange={handleSlugChange}
              placeholder="your-business-name"
              className={`flex-1 px-3 py-2 border rounded-r-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${
                isAvailable === false ? 'border-red-300' : 
                isAvailable === true ? 'border-green-300' : 'border-gray-300'
              }`}
              disabled={isSaving}
            />
            <div className="ml-2 flex items-center">
              {isChecking && (
                <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
              )}
              {!isChecking && isAvailable === true && (
                <Check className="h-4 w-4 text-green-500" />
              )}
              {!isChecking && isAvailable === false && (
                <X className="h-4 w-4 text-red-500" />
              )}
            </div>
          </div>

          {/* Availability Message */}
          {availabilityMessage && (
            <p className={`mt-1 text-sm ${
              isAvailable ? 'text-green-600' : 'text-red-600'
            }`}>
              {availabilityMessage}
            </p>
          )}
        </div>

        {/* Suggestions */}
        {suggestions.length > 0 && (
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Suggestions:</p>
            <div className="flex flex-wrap gap-2">
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Full URL Preview */}
        {slug && (
          <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
            <p className="text-xs text-gray-500 mb-1">Full URL Preview:</p>
            <p className="text-sm font-mono text-gray-800 break-all">{fullUrl}</p>
          </div>
        )}

        {/* Generate Suggestions Button */}
        {businessName && (
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={generateSuggestion}
              disabled={generateSuggestionsMutation.isPending}
              className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700 underline disabled:opacity-50"
            >
              {generateSuggestionsMutation.isPending && (
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              )}
              Generate from business name
            </button>
          </div>
        )}

        {/* Guidelines */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
          <div className="flex items-start">
            <Info className="h-4 w-4 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-blue-700">
              <p className="font-medium mb-1">URL Guidelines:</p>
              <ul className="space-y-1">
                <li>• Use 3-50 characters</li>
                <li>• Only lowercase letters, numbers, and hyphens allowed</li>
                <li>• Cannot start or end with hyphens</li>
                <li>• Must be unique across all providers</li>
                <li>• Cannot be changed once set</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Action Button */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={!isAvailable || isSaving || !slug || updateSlugMutation.isPending}
            className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              isAvailable && slug && !isSaving && !updateSlugMutation.isPending
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {(isSaving || updateSlugMutation.isPending) ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {(isSaving || updateSlugMutation.isPending) ? 'Setting URL...' : 'Set This URL'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProviderSlugSetupForm;